import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable, Subject, takeUntil } from 'rxjs';

export interface ApiSelectOption {
    label: string;
    value: any;
}

@Component({
    selector: 'ashcorp-api-select',
    templateUrl: './api-select.component.html',
    styleUrls: ['./api-select.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AshcorpApiSelectComponent implements OnInit, OnChanges, OnDestroy {
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    @Input() form!: FormGroup;
    @Input() label = '';
    @Input() name = '';
    @Input() required = false;

    // Provide a function reference, for example: () => this._appsService.list('name', 'asc', 0, 500)
    @Input() fetchOptions!: () => Observable<any>;

    // Optional mapper for adapting service response to select options.
    @Input() mapOptions: ((response: any) => ApiSelectOption[]) | null = null;

    options: ApiSelectOption[] = [];

    get id(): string {
        return `api-select-${this.name || this.label || 'field'}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }

    get control(): AbstractControl | null {
        if (!this.form || !this.name) {
            return null;
        }

        return this.form.get(this.name);
    }

    get errorMessage(): string {
        const control = this.control;
        if (!control) {
            return '';
        }

        const serverError = control.getError('server');
        if (serverError) {
            return serverError;
        }

        if (control.hasError('required')) {
            return `${this.label || this.name} is required.`;
        }

        return '';
    }

    ngOnInit(): void {
        this._loadOptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['fetchOptions'] && !changes['fetchOptions'].firstChange) {
            this._loadOptions();
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private _loadOptions(): void {
        if (!this.fetchOptions) {
            this.options = [];
            return;
        }

        this.fetchOptions()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.options = this.mapOptions
                        ? this.mapOptions(response)
                        : this._defaultMapOptions(response);
                },
                error: () => {
                    this.options = [];
                }
            });
    }

    private _defaultMapOptions(response: any): ApiSelectOption[] {
        const source = Array.isArray(response)
            ? response
            : Array.isArray(response?.items)
                ? response.items
                : [];

        return source.map((item: any) => {
            if (typeof item === 'string' || typeof item === 'number') {
                return { label: String(item), value: item };
            }

            const label = item?.name ?? item?.label ?? item?.code ?? '';
            const value = item?.id ?? item?.code ?? label;

            return { label: String(label), value };
        });
    }
}
